import { useEffect, useState, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { clearPortalToken, getPortalToken } from '../services/api'
import { verifyToken } from '../services/portal'
import { ErrorState } from '@lipaflow/ui'

interface TokenGateProps {
  children: ReactNode
}

/**
 * Guards authenticated portal routes.
 *
 * The token is stored in memory (set by LandingPage after the initial
 * #token= verification). It is never re-appended to the URL.
 */
export default function TokenGate({ children }: TokenGateProps) {
  const token = getPortalToken()

  const [state, setState] = useState<'loading' | 'valid' | 'invalid' | 'unavailable'>(
    token ? 'loading' : 'invalid'
  )
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!token) {
      setState('invalid')
      return
    }

    let active = true
    setState('loading')
    verifyToken()
      .then(() => {
        if (active) setState('valid')
      })
      .catch((error: unknown) => {
        if (!active) return
        const status = (error as { response?: { status?: number } }).response?.status
        if (status === 401 || status === 403) {
          clearPortalToken()
          setState('invalid')
        } else {
          setState('unavailable')
        }
      })
    return () => { active = false }
  }, [token, attempt])

  if (state === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500 mx-auto mb-4" />
          <p className="text-stone-500">Verifying access...</p>
        </div>
      </div>
    )
  }

  if (state === 'invalid') {
    return <Navigate to="/view" replace />
  }

  if (state === 'unavailable') return <ErrorState title="Unable to verify access"
    description="We couldn't reach the service. Try again to continue with your existing sign-in."
    onRetry={() => { setState('loading'); setAttempt((value) => value + 1) }} />

  return <>{children}</>
}
